import React from "react";

/**
 * SoccerPitch — realistic top-down pitch background used for lineup visualization.
 * Renders SVG lines (stripes, penalty boxes, center circle, corner arcs) and
 * accepts `children` as absolutely-positioned player slots.
 *
 * Props:
 *  - className: additional CSS classes for the outer container
 *  - aspectRatio: string (default "3/4")
 *  - children: React nodes rendered on top of the pitch
 */
export default function SoccerPitch({ className = "", aspectRatio = "3/4", children }) {
  return (
    <div
      className={`relative rounded-2xl overflow-hidden shadow-inner ${className}`}
      style={{ aspectRatio, background: "linear-gradient(180deg, #16a34a 0%, #15803d 50%, #166534 100%)" }}
      data-testid="soccer-pitch"
    >
      {/* Horizontal grass stripes */}
      <div className="absolute inset-0 pointer-events-none opacity-40"
           style={{
             backgroundImage: "repeating-linear-gradient(180deg, rgba(0,0,0,0.08) 0 5%, rgba(255,255,255,0.05) 5% 10%)",
           }} />

      {/* SVG pitch lines drawn as viewport-scalable overlay */}
      <svg
        viewBox="0 0 100 140"
        preserveAspectRatio="none"
        className="absolute inset-0 w-full h-full pointer-events-none"
        aria-hidden="true"
      >
        <g fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="0.6" strokeLinecap="round">
          {/* Outer border */}
          <rect x="3" y="3" width="94" height="134" rx="1.2" />
          {/* Halfway line */}
          <line x1="3" y1="70" x2="97" y2="70" />
          {/* Center circle + spot */}
          <circle cx="50" cy="70" r="10" />
          <circle cx="50" cy="70" r="0.9" fill="rgba(255,255,255,0.85)" />
          {/* Top penalty area (attack side) */}
          <rect x="22" y="3" width="56" height="18" />
          {/* Top goal area */}
          <rect x="35" y="3" width="30" height="7" />
          {/* Top penalty spot + arc */}
          <circle cx="50" cy="14" r="0.8" fill="rgba(255,255,255,0.85)" />
          <path d="M 40 21 A 10 10 0 0 0 60 21" />
          {/* Top goal */}
          <rect x="43" y="1" width="14" height="2" fill="rgba(255,255,255,0.35)" />

          {/* Bottom penalty area (goalkeeper side) */}
          <rect x="22" y="119" width="56" height="18" />
          {/* Bottom goal area */}
          <rect x="35" y="130" width="30" height="7" />
          {/* Bottom penalty spot + arc */}
          <circle cx="50" cy="126" r="0.8" fill="rgba(255,255,255,0.85)" />
          <path d="M 40 119 A 10 10 0 0 1 60 119" />
          {/* Bottom goal */}
          <rect x="43" y="137" width="14" height="2" fill="rgba(255,255,255,0.35)" />

          {/* Corner arcs */}
          <path d="M 3 5 A 2 2 0 0 0 5 3" />
          <path d="M 97 5 A 2 2 0 0 1 95 3" />
          <path d="M 3 135 A 2 2 0 0 1 5 137" />
          <path d="M 97 135 A 2 2 0 0 0 95 137" />
        </g>
      </svg>

      {children}
    </div>
  );
}
